import { APPLICANT_QUESTIONS, FIELD_PATHS, PersonType } from '@funnels/constants';
import { useCustomerFormApplicant, useFormValues } from '@funnels/hooks';
import { ApplicantEntityIndex, Asset, PostProductSelectContract, RelatedParty } from '@funnels/types';
import {
  ActionButtonsWrapper,
  Container,
  ContentWrapper,
  FunnelGrid,
  FunnelGridUnit,
  HeadingWrapper,
  NextArrowAction,
} from '@funnels/ui-components';
import {
  AddressCopier,
  Citizenship,
  CityOfBirth,
  ContactFirstName,
  ContactLastName,
  ContactTitle,
  CountryOfBirth,
  MothersMaidenName,
  PostSettlementMailingAddress,
} from '@funnels/ui-components/PostProductSelect';
import { getApplicantAddresses, getQuestionDisplayFields, maskPIIFullStory } from '@funnels/utils';
import { Heading } from '@lendi-ui/typography';
import { cloneDeep, get } from 'lodash';

interface ApplicantLayoutProps extends ApplicantEntityIndex {
  isFromSummary?: boolean;
}

export const ApplicantLayout = (props: ApplicantLayoutProps) => {
  const [formValues, setFormValue] = useFormValues();
  const { applicantIndex, isFromSummary } = props;
  const loggedInApplicant = useCustomerFormApplicant();
  const isLoggedInApplicant =
    loggedInApplicant?.customerId === get(formValues, FIELD_PATHS.APPLICANT_CUSTOMER_ID(applicantIndex));
  const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
  const applicantFirstName = get(formValues, FIELD_PATHS.APPLICANT_FIRST_NAME(applicantIndex));
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
  const ppsContractIndex = ppsContracts.findIndex(
    (pps) => get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)) === applicantId
  );
  const questions = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(ppsContractIndex));
  const hasQuestion = getQuestionDisplayFields<APPLICANT_QUESTIONS>(questions);
  const hasPersonalDetailsSection =
    hasQuestion.COUNTRY_OF_BIRTH ||
    hasQuestion.COUNTRY_OF_CITIZENSHIP ||
    hasQuestion.MOTHERS_MAIDEN_NAME ||
    hasQuestion.CITY_OF_BIRTH;
  const hasSpouseDetailsSection = hasQuestion.SPOUSE_FIRST_NAME || hasQuestion.SPOUSE_LAST_NAME;
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const spouseRelatedPartyIndex = relatedParties.findIndex(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === applicantId &&
      relatedParty.type === PersonType.Spouse
  );
  const assets = get(formValues, FIELD_PATHS.ASSETS(), []);
  const currentAddress = get(formValues, FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_RESIDENTIAL_FORMATTED(applicantIndex));
  const mailingAddress = get(formValues, FIELD_PATHS.APPLICANT_MAILING_ADDRESS_FORMATTED(applicantIndex));
  const currentPostSettlementAddress = get(
    formValues,
    FIELD_PATHS.APPLICANT_POST_SETTLEMENT_ADDRESS_FORMATTED(applicantIndex),
    ''
  );
  const applicantAddresses = getApplicantAddresses(get(formValues, FIELD_PATHS.APPLICANT(applicantIndex)), assets);

  const heading = isLoggedInApplicant ? (
    'We need more information about you'
  ) : (
    <>We need more information about {maskPIIFullStory(applicantFirstName)}</>
  );

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
      </HeadingWrapper>
      <ContentWrapper>
        {hasPersonalDetailsSection && (
          <>
            <FunnelGridUnit mt="sm">
              <Heading size="xs">Personal Details</Heading>
            </FunnelGridUnit>
            {hasQuestion.COUNTRY_OF_BIRTH && (
              <FunnelGridUnit mt="sm">
                <CountryOfBirth applicantIndex={applicantIndex} isOptional />
              </FunnelGridUnit>
            )}
            {hasQuestion.CITY_OF_BIRTH && (
              <FunnelGridUnit mt="sm">
                <CityOfBirth applicantIndex={applicantIndex} isOptional />
              </FunnelGridUnit>
            )}
            {hasQuestion.COUNTRY_OF_CITIZENSHIP && (
              <FunnelGridUnit mt="sm">
                <Citizenship applicantIndex={applicantIndex} isOptional />
              </FunnelGridUnit>
            )}
            {hasQuestion.MOTHERS_MAIDEN_NAME && (
              <FunnelGridUnit mt="sm">
                <MothersMaidenName applicantIndex={applicantIndex} isOptional />
              </FunnelGridUnit>
            )}
          </>
        )}

        {hasSpouseDetailsSection && (
          <>
            <FunnelGridUnit mt="sm">
              <Heading size="xs">Spouse details</Heading>
            </FunnelGridUnit>
            {(hasQuestion.SPOUSE_TITLE || hasQuestion.SPOUSE_FIRST_NAME) && (
              <FunnelGridUnit mt="sm">
                <FunnelGrid gutter="xxs">
                  <FunnelGridUnit size={hasQuestion.SPOUSE_TITLE && hasQuestion.SPOUSE_FIRST_NAME ? 0.3 : 1}>
                    {hasQuestion.SPOUSE_TITLE && (
                      <ContactTitle relatedPartyIndex={spouseRelatedPartyIndex} isOptional />
                    )}
                  </FunnelGridUnit>
                  <FunnelGridUnit size={hasQuestion.SPOUSE_TITLE && hasQuestion.SPOUSE_FIRST_NAME ? 0.7 : 1}>
                    {hasQuestion.SPOUSE_FIRST_NAME && (
                      <ContactFirstName relatedPartyIndex={spouseRelatedPartyIndex} isOptional />
                    )}
                  </FunnelGridUnit>
                </FunnelGrid>
              </FunnelGridUnit>
            )}
            {hasQuestion.SPOUSE_LAST_NAME && (
              <FunnelGridUnit mt="sm">
                <ContactLastName relatedPartyIndex={spouseRelatedPartyIndex} isOptional />
              </FunnelGridUnit>
            )}
          </>
        )}

        {hasQuestion.POST_SETTLEMENT_MAILING_ADDRESS && (
          <>
            <FunnelGridUnit mt="sm">
              <Heading size="xs">Post-settlement</Heading>
            </FunnelGridUnit>
            {!!applicantAddresses.length && (
              <FunnelGridUnit mt="sm">
                <AddressCopier
                  applicantIndex={applicantIndex}
                  addresses={applicantAddresses}
                  currentPostSettlementAddress={currentPostSettlementAddress}
                  setPostSettlementAddress={(address) => {
                    if (address === currentAddress) {
                      setFormValue(
                        FIELD_PATHS.APPLICANT_POST_SETTLEMENT_ADDRESS(applicantIndex),
                        cloneDeep(get(formValues, FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_RESIDENTIAL(applicantIndex)))
                      );
                    } else if (address === mailingAddress) {
                      setFormValue(
                        FIELD_PATHS.APPLICANT_POST_SETTLEMENT_ADDRESS(applicantIndex),
                        cloneDeep(get(formValues, FIELD_PATHS.APPLICANT_MAILING_ADDRESS(applicantIndex)))
                      );
                    } else {
                      const assetIndex = assets.findIndex(
                        (asset: Asset) => get({ assets: [asset] }, FIELD_PATHS.ASSET_FORMATTED_ADDRESS(0)) === address
                      );
                      setFormValue(
                        FIELD_PATHS.APPLICANT_POST_SETTLEMENT_ADDRESS(applicantIndex),
                        cloneDeep(get(formValues, FIELD_PATHS.ASSET_ADDRESS(assetIndex)))
                      );
                    }
                  }}
                />
              </FunnelGridUnit>
            )}
            <FunnelGridUnit mt="sm">
              <PostSettlementMailingAddress applicantIndex={applicantIndex} isOptional />
            </FunnelGridUnit>
          </>
        )}
      </ContentWrapper>
      <ActionButtonsWrapper>
        <NextArrowAction variant={'primary'} isFullWidth customText={isFromSummary ? 'Save' : 'Next'} />
      </ActionButtonsWrapper>
    </Container>
  );
};
